<template>
  <section>
    <b-row
      align-h="between"
      class="mb-3"
    >
      <b-col
        cols="auto"
      >
        <base-filter-dropdown
          v-if="isGroupReporting"
          :filter="'campaigns'"
          :options-list="campaignList"
          :key-property="'id'"
          :value-property="'name'"
          :selected-options="selectedCampaignIds"
          @updatedSelectedOptions="updatedCampaignFilterSelectedOptions('selectedCampaignIds', ...arguments)"
          @selectAllOptions="selectAllOptions('campaignList', 'selectedCampaignIds', 'id')"
          @deselectAllOptions="deselectAllOptions('selectedCampaignIds')"
        />
        <base-filter-dropdown
          class="ml-1"
          :filter="'publisher'"
          :options-list="campaignPublishersList"
          :key-property="'id'"
          :value-property="'name'"
          :selected-options="selectedPublishersIds"
          @updatedSelectedOptions="updatedSelectedOptions('selectedPublishersIds', ...arguments)"
          @selectAllOptions="selectAllOptions('campaignPublishersList', 'selectedPublishersIds', 'id')"
          @deselectAllOptions="deselectAllOptions('selectedPublishersIds')"
        />
        <base-filter-dropdown
          class="ml-1"
          :filter="'creative'"
          :options-list="uniqueCreatives"
          :key-property="'ids'"
          :value-property="'name'"
          :selected-options="selectedCreativesIds"
          @updatedSelectedOptions="updatedSelectedOptions('selectedCreativesIds', ...arguments)"
          @selectAllOptions="selectAllOptions('uniqueCreatives', 'selectedCreativesIds', 'ids')"
          @deselectAllOptions="deselectAllOptions('selectedCreativesIds')"
        />
        <base-filter-dropdown
          class="ml-1"
          :filter="'content items'"
          :options-list="uniqueContentItems"
          :key-property="'encodedExternalIds'"
          :value-property="'title'"
          :selected-options="selectedContentItemIds"
          @updatedSelectedOptions="updatedSelectedOptions('selectedContentItemIds', ...arguments)"
          @selectAllOptions="selectAllOptions('uniqueContentItems', 'selectedContentItemIds', 'encodedExternalIds')"
          @deselectAllOptions="deselectAllOptions('selectedContentItemIds')"
        />
        <base-filter-dropdown
          v-if="tab === 'Geo Performance'"
          class="ml-1"
          :filter="'Geo Locations'"
          :options-list="uniqueGeoLocations"
          :key-property="'ids'"
          :value-property="'name'"
          :selected-options="selectedGeoLocations"
          @updatedSelectedOptions="updatedSelectedOptions('selectedGeoLocations', ...arguments)"
          @selectAllOptions="selectAllOptions('uniqueGeoLocations', 'selectedGeoLocations', 'ids')"
          @deselectAllOptions="deselectAllOptions('selectedGeoLocations')"
        />
      </b-col>
      <b-col
        cols="auto"
      >
        <date-picker-dropdown
          @updateDates="updateDates"
        />
      </b-col>
    </b-row>
  </section>
</template>

<script>
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as contentTypes from '@/modules/content/store/types';
import * as reportingTypes from '@/modules/reporting/store/types';

import BaseFilterDropdown from '@/components/BaseFilterDropdown.vue';
import DatePickerDropdown from '@/components/DatePickerDropdown.vue';
import { mapActions, mapMutations, mapState } from 'vuex';

import { EventBus } from '@/core/eventBus';

export default {
  name: 'Filters',
  components: {
    DatePickerDropdown,
    BaseFilterDropdown,
  },
  props: {
    tab: {
      type: String,
      required: false,
      default: '',
    },
  },
  data: () => ({
    startDate: null,
    endDate: null,
    campaignList: [],
    campaignPublishersList: [],
    campaignContentItems: [],
    geoLocations: [],
    selectedCampaignIds: [],
    selectedPublishersIds: [],
    selectedCreativesIds: [],
    selectedContentItemIds: [],
    selectedGeoLocations: [],
  }),
  computed: {
    ...mapState('campaigns', [
      'campaign',
    ]),
    ...mapState('content', [
      'creatives',
    ]),
    ...mapState('content', [
      'feedId',
    ]),
    feedIdsFromCreatives() {
      return [...new Set(this.creatives
        .map((creative) => creative.feedId))];
    },
    isGroupReporting() {
      return this.$route.name === 'MultipleReportingIndex';
    },
    uniqueCreatives() {
      return Object.values(this.creatives.reduce((acc, item) => {
        const key = item.name;
        if (!acc[key]) {
          acc[key] = { name: key, ids: [] };
        }
        acc[key].ids.push(item.id);
        return acc;
      }, {}));
    },
    uniqueContentItems() {
      return Object.values(this.campaignContentItems.reduce((acc, item) => {
        const key = item.title;
        if (!acc[key]) {
          acc[key] = { title: key, encodedExternalIds: [] };
        }
        acc[key].encodedExternalIds.push(item.encodedExternalId);
        return acc;
      }, {}));
    },
    uniqueGeoLocations() {
      return Object.values(this.geoLocations.reduce((acc, item) => {
        const key = item.name;
        if (!acc[key]) {
          acc[key] = { name: key, ids: [] };
        }
        acc[key].ids.push(item.id);
        return acc;
      }, {}));
    },
  },
  methods: {
    ...mapActions('campaigns', [
      campaignTypes.FETCH_PUBLISHERS,
      campaignTypes.FETCH_CAMPAIGNS_FOR_GROUP,
      campaignTypes.FETCH_GEO_LOCATIONS,
      campaignTypes.FETCH_ORDERS,
    ]),
    ...mapActions('content', [
      contentTypes.FETCH_LIVE_CONTENT_ITEMS,
      contentTypes.FETCH_EXISTING_CREATIVES_AS_SHAREE,
    ]),
    ...mapActions('reporting', [
      reportingTypes.FETCH_PUBLISHER_IDS_WITH_STATS,
      reportingTypes.FETCH_GEO_LOCATION_IDS_WITH_STATS,
    ]),
    ...mapMutations('campaigns', [
      campaignTypes.SET_GEO_LOCATIONS,
    ]),
    updatedSelectedOptions(selectedOptionsList, id, isChecked) {
      isChecked ? this[selectedOptionsList].push(id)
        : this[selectedOptionsList].splice(this[selectedOptionsList].indexOf(id), 1);

      this.updateReport();
    },
    updatedCampaignFilterSelectedOptions(selectedOptionsList, id, isChecked) {
      isChecked ? this[selectedOptionsList].push(id)
        : this[selectedOptionsList].splice(this[selectedOptionsList].indexOf(id), 1);

      this.fetchDropdownData(false);
      this.updateReport();
    },
    initialiseOptions(fullList, selectedOptionsList, keyProperty) {
      this[selectedOptionsList] = this[fullList].map((option) => option[keyProperty]);
      if (selectedOptionsList === 'selectedCampaignIds') {
        this.fetchDropdownData(false);
      }
    },
    selectAllOptions(fullList, selectedOptionsList, keyProperty) {
      this.initialiseOptions(fullList, selectedOptionsList, keyProperty);
      this.updateReport();
    },
    deselectAllOptions(selectedOptionsList) {
      this[selectedOptionsList] = [];
      this.updateReport();
    },
    fetchCampaigns() {
      if (!this.isGroupReporting) {
        return [this.campaign];
      }
      const payload = { advertiserId: this.$route.params.advertiserId, group: this.$route.params.group };
      return this.FETCH_CAMPAIGNS_FOR_GROUP(payload);
    },
    fetchPublishers() {
      return this.FETCH_PUBLISHERS({ all: true });
    },
    fetchPublisherIdsWithStats() {
      const payload = {
        creativeIds: this.selectedCreativesIds.join(','),
      };
      return this.FETCH_PUBLISHER_IDS_WITH_STATS(payload);
    },
    fetchContentItems() {
      const payload = {
        advertiserId: this.campaign.advertiserId,
        feedIds: this.feedIdsFromCreatives,
      };
      return this.FETCH_LIVE_CONTENT_ITEMS(payload);
    },
    fetchGeoLocations() {
      return this.FETCH_GEO_LOCATIONS();
    },
    fetchGeoLocationIdsWithStats() {
      const payload = {
        campaignIds: this.selectedCampaignIds,
      };
      return this.FETCH_GEO_LOCATION_IDS_WITH_STATS(payload);
    },
    createCampaignList(fullCampaignList) {
      this.campaignList = fullCampaignList.filter((campaign) => campaign.state !== 'DRAFT' && campaign.state !== 'REVIEW' && campaign.state !== 'HIDDEN');
    },
    createPublisherList(fullPublishersList, publishersIdsWithStats) {
      this.campaignPublishersList = fullPublishersList.flat()
        .filter((publisher) => publishersIdsWithStats.includes(publisher.id));
      this.$emit('setCampaignPublishersList', this.campaignPublishersList);
    },
    createCampaignItems(contentItems) {
      this.campaignContentItems = contentItems;
    },
    createGeoLocationItems(geoLocations, geoLocationsWithStats) {
      this.SET_GEO_LOCATIONS(geoLocations.countries);
      this.geoLocations = geoLocations.countries
        .filter((geoLocation) => geoLocationsWithStats.geoLocationIds.includes(geoLocation.id));
    },
    updateDates(startDate, endDate) {
      this.$emit('updateDates', startDate, endDate);
      this.startDate = startDate;
      this.endDate = endDate;
      this.updateReport();
    },
    updateReport() {
      if (this.selectedCampaignIds.length > 0 && this.selectedPublishersIds.length > 0 && this.selectedCreativesIds.length > 0 && this.selectedContentItemIds.length > 0) {
        this.$emit('setPageLoading', true);
        const filterData = {
          fromDate: this.startDate,
          toDate: this.endDate,
          campaignIds: this.selectedCampaignIds,
          creativeIds: this.selectedCreativesIds.length === this.uniqueCreatives.length ? [] : this.selectedCreativesIds.flat(),
          publisherIds: this.selectedPublishersIds.length === this.campaignPublishersList.length ? [] : this.selectedPublishersIds,
          contentItemIds: this.selectedContentItemIds.length === this.uniqueContentItems.length ? [] : this.selectedContentItemIds.flat(),
          geoIds: this.selectedGeoLocations.flat(),
          areAllGeosSelected: this.selectedGeoLocations.length === this.uniqueGeoLocations.length,
        };
        this.$emit('updateReport', filterData);
        EventBus.$emit('data-update', filterData);
        this.$emit('setPageLoading', false);
      } else {
        this.$emit('updateReport', []);
      }

      this.$emit('areAllParametersPresent',
        this.selectedCampaignIds.length > 0
        && this.selectedPublishersIds.length > 0
        && this.selectedCreativesIds.length > 0
        && this.selectedContentItemIds.length > 0);
    },
    async fetchDropdownData(shouldRefreshCampaignOptions) {
      try {
        if (shouldRefreshCampaignOptions) {
          const campaigns = await this.fetchCampaigns();
          this.createCampaignList(campaigns);
          this.selectAllOptions('campaignList', 'selectedCampaignIds', 'id');

          const { advertiserId } = this.$route.params;

          this.FETCH_ORDERS({ advertiserId, campaignIds: this.selectedCampaignIds });
        } else if (this.selectedCampaignIds.length > 0) {
          this.selectedCreativesIds = [];
          this.selectedPublishersIds = [];
          this.selectedContentItemIds = [];
          this.selectedGeoLocations = [];
          await this.FETCH_EXISTING_CREATIVES_AS_SHAREE([this.selectedCampaignIds]);
          this.selectAllOptions('uniqueCreatives', 'selectedCreativesIds', 'ids');
          const [fullPublishersList, publisherIdsWithStats] = await Promise.all([
            this.fetchPublishers(),
            this.fetchPublisherIdsWithStats(),
          ]);
          this.createPublisherList(fullPublishersList, publisherIdsWithStats);
          this.selectAllOptions('campaignPublishersList', 'selectedPublishersIds', 'id');
          const contentItem = await this.fetchContentItems();
          this.createCampaignItems(contentItem);
          this.selectAllOptions('uniqueContentItems', 'selectedContentItemIds', 'encodedExternalIds');

          const [geoLocationsResponse, geoLocationIdsWithStatsResponse] = await Promise.all([
            this.fetchGeoLocations(),
            this.fetchGeoLocationIdsWithStats(),
          ]);
          this.createGeoLocationItems(geoLocationsResponse, geoLocationIdsWithStatsResponse);
          this.selectAllOptions('uniqueGeoLocations', 'selectedGeoLocations', 'ids');

          this.updateReport();
        }
      } catch (e) {
        this.$router.back();
      }
    },
  },
  async created() {
    await this.fetchDropdownData(true);
  },
};
</script>
